import React, { useState, useEffect } from 'react';
import { AdminApi } from '../../services/adminApi';
import TestAskAiSkillAttach from '../skills/TestAskAiSkillAttach';

interface AdminTestViewerProps {
  paperId: number;
}

const AdminTestViewer: React.FC<AdminTestViewerProps> = ({ paperId }) => {
  const [testData, setTestData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [skillQuestionId, setSkillQuestionId] = useState<number | null>(null);

  useEffect(() => {
    fetchTestDetails();
  }, [paperId]);

  const fetchTestDetails = async () => {
    setLoading(true);
    try {
      const response = await AdminApi.getAdminTestDetails(paperId);
      if (response.success) {
        setTestData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch admin test details', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-10 text-m2-on-surface-variant">Đang tải bài thi...</div>;
  if (!testData) return <div className="text-center py-10 text-red-500">Không tìm thấy thông tin bài thi hoặc chưa gắn đề thi.</div>;

  const renderQuestion = (qData: any, idx: number, prefix: string = '') => {
    if (!qData) return null;
    const qType = qData.question_type;
    const qId = qData.id;
    const label = prefix ? `${prefix}.${idx + 1}` : `Câu ${idx + 1}`;
    const skillOpen = skillQuestionId === qId;

    return (
      <div key={qId} className="mb-6 pb-6 border-b border-m2-outline-variant last:border-0 last:pb-0">
        <div className="flex justify-between items-start mb-3 gap-3">
          <h4 className="font-medium text-m2-on-surface min-w-0">
            <span className="font-bold mr-2">{label}:</span>
            {qData.content.replace(/<[^>]*>?/gm, '')}
          </h4>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <span className="text-xs bg-m2-surface-high text-m2-on-surface-variant px-2 py-1 rounded-full whitespace-nowrap">
              {qType}
            </span>
            <button
              type="button"
              onClick={() => setSkillQuestionId(skillOpen ? null : qId)}
              className={`text-xs font-semibold rounded-md border px-2 py-1 ${
                skillOpen
                  ? 'border-m2-primary bg-m2-primary/10 text-m2-primary'
                  : 'border-m2-outline-variant text-m2-on-surface-variant hover:border-m2-primary/40'
              }`}
            >
              {skillOpen ? 'Đóng skill câu' : 'Gắn skill câu'}
            </button>
          </div>
        </div>

        {skillOpen && (
          <div className="mb-3">
            <TestAskAiSkillAttach
              scope="question"
              ownerId={qId}
              title={`Skill Ask AI — ${label}`}
              compact
            />
          </div>
        )}

        {(qType === 'MULTIPLE_CHOICE' || qType === 'SINGLE_CHOICE' || qData.answers?.length > 0) ? (
          <div className="space-y-2 pl-4 mt-3">
            {qData.answers.map((ans: any) => (
              <div
                key={ans.id}
                className={`flex items-center space-x-3 p-2 rounded border ${
                  ans.is_correct
                    ? 'bg-green-50 border-green-200'
                    : 'bg-m2-surface-container border-m2-outline-variant opacity-70'
                }`}
              >
                <div className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center ${
                  ans.is_correct ? 'bg-green-500 text-white' : 'border border-m2-outline text-transparent'
                }`}>
                  {ans.is_correct && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className={`text-sm ${ans.is_correct ? 'text-green-800 font-medium' : 'text-m2-on-surface-variant'}`}>
                  {ans.content.replace(/<[^>]*>?/gm, '')}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {qType === 'ESSAY' && (
          <div className="mt-3 pl-4">
            <div className="p-3 bg-m2-surface-low border border-dashed border-m2-outline rounded text-sm text-m2-on-surface-variant italic">
              Câu hỏi tự luận. Học viên sẽ nhập văn bản trả lời ở đây.
            </div>
          </div>
        )}
      </div>
    );
  };

  const ungroupedQuestions = testData.questions.filter((pq: any) => !pq.group_id);
  const groupedQuestions = testData.groups.map((g: any) => ({
    ...g,
    questions: testData.questions.filter((pq: any) => pq.group_id === g.id)
  }));

  return (
    <div className="bg-m2-surface-container rounded p-6 shadow-sm border border-m2-outline-variant space-y-6">
      <div className="border-b border-m2-outline-variant pb-4">
        <h2 className="text-xl font-bold text-m2-on-surface">{testData.name}</h2>
        <div className="flex space-x-4 mt-2 text-sm text-m2-on-surface-variant">
          <span>Thời gian: {testData.minute} phút</span>
          <span>Điểm tối đa: {testData.max_point}</span>
        </div>
      </div>

      <div className="rounded-xl border-2 border-m2-primary/40 bg-m2-primary/5 p-4 space-y-3">
        <div>
          <h3 className="text-base font-bold text-m2-primary">Gắn skill Ask AI cho bài test này</h3>
          <p className="mt-1 text-xs text-m2-on-surface-variant">
            Skill gắn ở đây dùng chung mọi câu. Muốn skill riêng từng câu → bấm nút “Gắn skill câu” bên cạnh mỗi câu bên dưới.
          </p>
        </div>
        <TestAskAiSkillAttach scope="paper" ownerId={paperId} title="Skill cấp bài test (paper)" />
      </div>

      <div className="space-y-8">
        {ungroupedQuestions.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-m2-on-surface mb-4 border-l-4 border-m2-primary pl-3">Trắc nghiệm / Tự luận</h3>
            <div className="bg-m2-surface-container border border-m2-outline-variant rounded-lg p-5">
              {ungroupedQuestions.map((pq: any, i: number) => renderQuestion(pq.questionData, i))}
            </div>
          </div>
        )}

        {groupedQuestions.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-m2-on-surface mb-4 border-l-4 border-purple-500 pl-3">Bài tập tình huống</h3>
            {groupedQuestions.map((g: any, gIdx: number) => (
              <div key={g.id} className="mb-6 bg-m2-surface-container border border-purple-200 rounded-lg overflow-hidden">
                <div className="bg-purple-50 p-4 border-b border-purple-100">
                  <h4 className="font-bold text-purple-900 mb-2">Case Study {gIdx + 1}</h4>
                  <div
                    className="mt-2 text-sm text-m2-on-surface bg-m2-surface-container p-3 rounded border border-purple-100 prose prose-sm max-w-none prose-img:max-w-full"
                    dangerouslySetInnerHTML={{ __html: g.description || g.name || '' }}
                  />
                </div>
                <div className="p-5">
                  <h5 className="font-bold text-m2-on-surface mb-4 text-sm uppercase tracking-wider text-purple-600">Câu hỏi:</h5>
                  {g.questions.map((pq: any, i: number) => renderQuestion(pq.questionData, i, `Câu ${gIdx + 1}`))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestViewer;
